import React from 'react';
import { Button } from '@material-ui/core'
import { Add, DeleteForever } from '@material-ui/icons'
import { CustomSkill } from '../../models/AgentSkills/CustomSkill';
import styles from './styles.module.css'
import { AgentSkillsActions } from '../../actions/AgentSkillsActions';
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css';
import { GreyBlueBtn } from '../Styled/StyledButtons';
import AddCustomSkillsComponent from './AddCustomSkillsComponent';
import { AgentCustomSkill } from '../../models/AgentSkills/AgentCustomSkill';
import DeleteSkillApiError from '../../models/ApiErrors/DeleteSkillApiError';
import CustomSkillDeleteComponent from './CustomSkillDeleteComponent';
import {
  SKILL_TYPE_BOOL,
  SKILL_TYPE_ENUM,
  SKILL_TYPE_INT,
  SKILL_TYPE_STRING,
} from '../../models/AgentSkills/CustomSkillsTypes';

interface IState {
  skillsOpen: boolean,
  newSkills: CustomSkill[],
  skillDeleteShow: boolean,

  skillDeleteError: DeleteSkillApiError,
  skillForDelete: CustomSkill
}

export interface IProps {
  skills: CustomSkill[],
  agentSkills: AgentCustomSkill[],
  skillsReload: () => void
  handleChange: (skillId: number, value: string) => void
  handleChangeAvailability: (skillId: number, avaliable: boolean) => void
}

/**
 * The per-agent value of every custom skill.
 *
 * One widget per skill type (see models/AgentSkills/CustomSkillsTypes.ts for the vocabulary):
 * text, number, a checkbox that writes "0"/"1", and a select over the enum's declared values.
 * A type id this build does not know degrades to a text box *plus a visible warning* - the
 * screen never renders the type name blind, and never accepts a value without saying what rule
 * it was (or was not) checked against.
 *
 * The validity rule itself lives on CustomSkill.ValueError so that the red border here and the
 * refusal in AgentSkillsComponent.updateAgent() are the same decision, not two copies of it.
 */
export default class CustomSkillsComponent extends React.Component<IProps, IState> {

  state: IState = {
    skillsOpen: false,
    newSkills: [],

    skillDeleteShow: false,
    skillDeleteError: new DeleteSkillApiError(),
    skillForDelete: new CustomSkill()
  }

  newSkillsOpen: boolean = false

  modalSkillsOpen() {
    this.setState({ skillsOpen: true })
  }

  toggleChildMenu() {
    this.setState({ skillsOpen: !this.state.skillsOpen })
  }

  deleteSkill(skill: CustomSkill) {
    AgentSkillsActions.deleteSkill(skill.Id)
      .then((data) => {
        if (data instanceof CustomSkill) {
          this.props.skillsReload()
        } else {
          this.setState({ skillDeleteShow: true, skillDeleteError: data, skillForDelete: skill })
        }
      })

  }
  closeDeleteSkillError() {
    this.setState({ skillDeleteShow: false })
  }
  handleSkillsAdd() {
    this.props.skillsReload()
    this.toggleChildMenu()
  }
  public FindSkill(id: number): number {
    for (let i = 0; i < this.props.agentSkills.length; i++) {
      if (this.props.agentSkills[i].Id === id) {
        return i
      }
    }
    return -1
  }
  isAllChecked() {
    let isAll = true
    this.props.skills.forEach((skill) => {
      if (this.FindSkill(skill.Id) < 0) {
        isAll = false
      }
    })
    return isAll;
  }
  handleCheckedAll() {
    let checked = true
    if (this.isAllChecked()) {
      checked = false
    }
    this.props.skills.forEach(skill => {
      this.props.handleChangeAvailability(skill.Id, checked)
    });
  }
  /**
   * Was `/[0-9a-zA-Z_]$|^$/g` + `re.test(value)`: unanchored, so it only ever looked at the
   * last character and "ab!c" sailed through. The rule it was reaching for is "the whole value
   * is letters, digits and underscores", and it now belongs to the type - a number has to be
   * allowed to contain "." and "-", and an enum value is picked rather than typed.
   */
  handleValue(skill: CustomSkill, value: string) {
    if (skill.AcceptsInput(value)) {
      this.props.handleChange(skill.Id, value)
    }
  }

  agentValue(skillId: number): string {
    const index = this.FindSkill(skillId)
    return (index < 0) ? '' : this.props.agentSkills[index].Value
  }

  renderError(message: string) {
    if (message.length === 0) {
      return null
    }
    return <div className={styles.CustomSkillError}>{message}</div>
  }

  renderValue(skill: CustomSkill) {
    const enabled = this.FindSkill(skill.Id) >= 0
    const value = this.agentValue(skill.Id)
    // An untouched row is not "wrong" yet, so only an attached skill is marked.
    const error = enabled ? skill.ValueError(value) : ''
    const controlClass = `${(error.length > 0) ? styles.FormInvalid : ""} ${styles.FormControl} ${styles.FullWidth}`

    switch (skill.TypeId) {
      case SKILL_TYPE_STRING:
        return (
          <div>
            <input placeholder="value" className={controlClass} value={value} disabled={!enabled} onChange={event => this.handleValue(skill, event.target.value)} />
            {this.renderError(error)}
          </div>
        )

      case SKILL_TYPE_INT:
        return (
          <div>
            <input placeholder="value" type="number" className={controlClass} value={value} disabled={!enabled} onChange={event => this.handleValue(skill, event.target.value)} />
            {this.renderError(error)}
          </div>
        )

      case SKILL_TYPE_BOOL:
        // Stored as text, like every other skill value, with "1"/"0" as the only two spellings
        // a queue expression can match on. A row that holds anything else (a skill created
        // before this screen knew about bools) shows unticked *and* red, so it cannot be saved
        // by accident - one click on either state writes a canonical value.
        return (
          <div>
            <label className={`${styles.SkillsCheckbox} ${(error.length > 0) ? styles.FormInvalid : ""}`}>
              <input type="checkbox" checked={value === '1'} disabled={!enabled} onChange={event => this.handleValue(skill, event.target.checked ? '1' : '0')} />
              <span className={`${(enabled) ? styles.CheckLabelChecked : styles.CheckLabel}`}>{(value === '1') ? 'yes' : 'no'}</span>
            </label>
            {this.renderError(error)}
          </div>
        )

      case SKILL_TYPE_ENUM:
        if (skill.Values.length === 0) {
          // The skill says "enum" but the API sent no vocabulary with it. Showing an empty
          // select would read as "no choice made"; this says why there is nothing to choose.
          return (
            <div>
              <input placeholder="value" className={`${styles.FormInvalid} ${styles.FormControl} ${styles.FullWidth}`} value={value} disabled={true} readOnly={true} />
              {this.renderError('no allowed values came back for this skill - it cannot be set until they are declared')}
            </div>
          )
        }
        return (
          <div>
            <select className={controlClass} value={value} disabled={!enabled} onChange={event => this.handleValue(skill, event.target.value)}>
              <option value="" disabled>choose value</option>
              {skill.Values.map((allowed) => (
                <option key={allowed} value={allowed}>{allowed}</option>
              ))}
              {/*
                A value stored before the list was edited would otherwise render as a blank
                select - the control would quietly misreport what the agent actually has.
              */}
              {(value.length > 0 && skill.Values.indexOf(value) < 0) ?
                <option key={value} value={value}>{value} (no longer allowed)</option>
                :
                null
              }
            </select>
            {this.renderError(error)}
          </div>
        )

      default:
        return (
          <div>
            <input placeholder="value" className={controlClass} value={value} disabled={!enabled} onChange={event => this.handleValue(skill, event.target.value)} />
            {this.renderError(`unrecognised skill type #${skill.TypeId} - this screen does not know its rule, the value is sent as plain text`)}
            {this.renderError(error)}
          </div>
        )
    }
  }

  render() {
    return (<div>
      <div className={mainStyles.ComponentHeader}><span>custom operational skills</span></div>
      <div className={mainStyles.ComponentBody}>
        <table className={styles.Table}>
          <thead>
            <tr>
              <th className={styles.BlackUnderlined}>
                <label className={styles.SkillsCheckbox}>
                  <input type="checkbox" value="all" checked={(this.isAllChecked())} onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleCheckedAll()} />
                  <span className={`${styles.CheckLabelChecked}`}>NAME</span>
                </label>
              </th>
              <th className={`${styles.ValueCol} ${styles.BlackUnderlined}`}><label className={styles.CheckLabelChecked}>VALUE</label></th>
              <th className={`${styles.DeleteCol} ${styles.BlackUnderlined}`}><label className={styles.CheckLabelChecked}>DELETE</label></th>
            </tr>
          </thead>
          <tbody>
            {this.props.skills.map((row: CustomSkill, index) => (
              <tr key={index} className={`${((this.FindSkill(row.Id) >= 0) ? styles.CheckLabelChecked : styles.CheckLabel)}`}>
                <td className={styles.Underlined}>
                  <label className={styles.SkillsCheckbox} key={row.Id} >
                    <input type="checkbox" value={row.Id} checked={(this.FindSkill(row.Id) >= 0)} onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.props.handleChangeAvailability(parseInt(event.target.value), event.target.checked)} />
                    <span>{row.Key}</span>
                  </label>
                </td>

                <td className={`${styles.ValueCol} ${styles.Underlined}`}>
                  {this.renderValue(row)}
                </td>
                <td className={`${styles.DeleteCol} ${styles.Underlined}`} ><Button color="secondary" onClick={() => { this.deleteSkill(row) }}><DeleteForever /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>

      </div>
      <div>
        <div className={styles.ButtonMargin}><GreyBlueBtn color="primary" fullWidth={true} onClick={() => this.modalSkillsOpen()}><Add /> add skills</GreyBlueBtn></div>
        <AddCustomSkillsComponent open={this.state.skillsOpen} addNewSkills={() => this.handleSkillsAdd()} toggle={() => this.toggleChildMenu()}></AddCustomSkillsComponent>
        <CustomSkillDeleteComponent open={this.state.skillDeleteShow} error={this.state.skillDeleteError} toggle={() => this.closeDeleteSkillError()} customSkill={this.state.skillForDelete} />
      </div>
    </div>)
  }
}
